import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "loading", "loadingVoiceOver", "iconSize"];
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { ICON_SIZES } from '../../constants';
import CustomPropTypes from '../../util/custom-prop-types';
import { LoadingMaskContent } from '../LoadingMaskContent/LoadingMaskContent';
import { LoadingMaskSpinner } from '../LoadingMaskSpinner/LoadingMaskSpinner';
import { Spinner } from '../Spinner/Spinner';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import { StyledLoadingMask } from './LoadingMask.styles';

/**
 * A container used for masking content while something is loading, submitting, processing, etc.
 *
 * The container uses an ARIA live region with the
 * [status role](https://www.w3.org/TR/wai-aria-1.1/#status). When `loading` is set to `true`, the
 * content will be masked visually and hidden from screen readers, and the `loadingVoiceOver` text
 * will be announced.
 *
 * @related ['LoadingButton']
 * @since 8.1.0
 */
export var LoadingMask = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children,
    loading = _ref.loading,
    loadingVoiceOver = _ref.loadingVoiceOver,
    iconSize = _ref.iconSize,
    rest = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(StyledLoadingMask, _extends({
    ref: forwardedRef
  }, rest), loading ? /*#__PURE__*/React.createElement(VisuallyHidden, null, loadingVoiceOver) : null, /*#__PURE__*/React.createElement(LoadingMaskContent, {
    loading: loading,
    "aria-hidden": loading
  }, children), /*#__PURE__*/React.createElement(LoadingMaskSpinner, {
    loading: loading,
    iconSize: iconSize
  }, /*#__PURE__*/React.createElement(Spinner, null)));
});
LoadingMask.displayName = 'LoadingMask';
LoadingMask.propTypes = {
  /**
   * The content to be masked.
   */
  children: PropTypes.node.isRequired,
  /**
   * Loading mask's wrapper CSS display value
   */
  display: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /**
   * The loading state of the container.
   */
  loading: PropTypes.bool,
  /**
   * The text that will be announced to screen readers using `VisuallyHidden`.
   */
  loadingVoiceOver: PropTypes.string,
  /**
   * The size of the `Spinner`.
   */
  iconSize: PropTypes.oneOf(Object.values(ICON_SIZES)),
  /**
   * The ARIA role, defaulting to [status](https://www.w3.org/TR/wai-aria-1.1/#status).
   */
  role: PropTypes.string
};
LoadingMask.defaultProps = {
  loading: true,
  loadingVoiceOver: 'Loading',
  iconSize: 'md',
  role: 'status',
  display: 'inline-flex'
};